import InlineSvg from 'components/InlineSvg';
import { useAppSelector } from 'lib/hooks/redux';
import { useSiteMapQuery } from 'lib/hooks/queries';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { calcStaticMapSize } from 'lib/utils';

interface Props {
  onLoadFinish: () => void;
}

const SiteMapCanvas = ({ onLoadFinish }: Props) => {
  // Redux States
  const { showTerrain } = useAppSelector((state) => ({
    showTerrain: state.siteMap.showTerrain,
  }));

  // Get Site Map Data from React-Query Cache (No Params)
  const siteMapData = useSiteMapQuery().data!;
  // Semantic Vars From Site Map Data
  const {
    smGroundOverlay,
    applicationURL: { BASE_ASSET_URL },
    community: { applicationID: communityId, smFolder, smFileName },
  } = siteMapData;

  // React/Custom Hooks
  const { height: winHeight, width: winWidth } = useWindowDimensions();
  const [isSvgLandscape, setIsSvgLandscape] = useState<boolean | undefined>();
  const [staticMapSize, setStaticMapSize] = useState<number>(0);
  const screenRatio = winWidth / winHeight;
  const svg = document.querySelector('#site-map-svg svg');

  useEffect(() => {
    if (svg) {
      const svgWidth = parseInt(svg.getAttribute('width')!);
      const svgHeight = parseInt(svg.getAttribute('height')!);

      let isSvgLandscape = svgWidth / svgHeight > screenRatio;
      setIsSvgLandscape(isSvgLandscape);
    }
  }, [svg, screenRatio]);

  useEffect(() => {
    if (isSvgLandscape !== undefined && smGroundOverlay.centerOfMapCoordinates) {
      setStaticMapSize(calcStaticMapSize(smGroundOverlay));
    }
  }, [isSvgLandscape, smGroundOverlay]);

  return (
    <StyledSiteMapCanvas isSvgLandscape={isSvgLandscape} isStacked={false}>
      <InlineSvg
        role="svg"
        id="site-map-svg"
        data-cy="site-map-svg"
        src={`${BASE_ASSET_URL}/${communityId}/${smFolder}/${smFileName}`}
        onLoad={onLoadFinish}
      />
      {showTerrain && (
        <StyledSiteMapOverlay
          src={`${BASE_ASSET_URL}/${communityId}/${smFolder}/terrainOverlay.png  `}
          staticMapSize={staticMapSize}
          style={{
            transform: `rotate(-${smGroundOverlay.terrainRotate}deg)`,
          }}
          alt="site-map-google-maps"
          data-cy="site-map-terrain"
        />
      )}
    </StyledSiteMapCanvas>
  );
};

export default SiteMapCanvas;

export const StyledSiteMapCanvas = styled.div<{ isSvgLandscape?: boolean; isStacked: boolean }>`
  margin: 0 auto;
  padding: 5vh 4vh 3vh 4vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: ${({ isStacked }) => (!isStacked ? '100vh' : '100%')};
  width: ${({ isStacked }) => (!isStacked ? '100vw' : '100%')};
  svg {
    ${({ isSvgLandscape }) =>
      isSvgLandscape !== undefined &&
      (isSvgLandscape ? `width: 100%; height: unset;` : `width: unset; height: 100%;`)}
  }
  .hovered-lot {
    path {
      stroke-width: 5px;
      stroke: ${({ theme }) => theme.colors.highlightColor};
    }
  }

  .hovered-building {
    path {
      stroke-width: 5px;
      stroke: ${({ theme }) => theme.colors.highlightColor};
    }
  }

  .hovered-unit {
    use {
      stroke-width: 5px;
      stroke: ${({ theme }) => theme.colors.highlightColor};
    }
  }

  @media (max-width: 567px), (max-height: 567px) {
    padding: 75px 0 0;
  }
`;

const StyledSiteMapOverlay = styled.img<{
  staticMapSize: number;
}>`
  position: absolute;
  z-index: -10;
  width: ${({ staticMapSize }) => `${staticMapSize}px`};
  height: ${({ staticMapSize }) => `${staticMapSize}px`};
`;
